import React, { useMemo } from "react";
import { 
  Calendar, 
  DollarSign, 
  ArrowLeft, 
  FileText, 
  CreditCard, 
  Banknote, 
  Receipt,
  CheckCircle,
  Clock,
  AlertCircle,
  TrendingUp
} from "lucide-react";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezone);
const PaymentHistory = ({ 
  payments = [], 
  onBack, 
  socio, 
  show = true
}) => {

  // Formatear moneda
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP'
    }).format(amount);
  };

  // Calcular el monto total de un pago desde sus cuotas sociales
  const calculatePaymentAmount = (payment) => {
    if (payment.amount && payment.amount !== null) {
      return parseFloat(payment.amount);
    }
    
    // Si amount es null, calculamos desde las cuotas sociales
    return payment.paymentSocialQuotas?.reduce((total, paymentQuota) => {
      const quotaAmount = parseFloat(paymentQuota.socialQuota?.amount || 0);
      return total + quotaAmount;
    }, 0) || 0;
  };

  // Obtener ícono del método de pago
  const getPaymentMethodIcon = (method) => {
    if (typeof method !== "string") {
      return <DollarSign size={16} className="text-gray-600" />;
    }
    switch (method.toLowerCase()) {
      case 'transferencia':
        return <CreditCard size={16} className="text-blue-600" />;
      case 'efectivo':
        return <Banknote size={16} className="text-green-600" />;
      case 'cheque':
        return <Receipt size={16} className="text-purple-600" />;
      case 'depósito':
        return <CreditCard size={16} className="text-indigo-600" />;
      default:
        return <DollarSign size={16} className="text-gray-600" />;
    }
  };

  // Traducir estados de cuotas
  const translateQuotaState = (status) => {
    const translations = {
      'paid': 'Pagada',
      'PAGADO': 'Pagada',
      'PENDIENTE': 'Pendiente',
      'pending': 'Pendiente',
      'overdue': 'Vencida',
      'cancelled': 'Cancelada',
      'cancelado': 'Cancelada',
      'processing': 'Procesando'
    };
    return translations[status] || status;
  };

  // Obtener badge del estado
  const getStatusBadge = (status) => {
    const configs = {
      completado: {
        icon: <CheckCircle size={12} />,
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        borderColor: 'border-green-200'
      },
      pendiente: {
        icon: <Clock size={12} />,
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        borderColor: 'border-yellow-200'
      },
      fallido: {
        icon: <AlertCircle size={12} />,
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        borderColor: 'border-red-200'
      },
      cancelado: {
        icon: <AlertCircle size={12} />,
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        borderColor: 'border-red-200'
      },
    };

    const config = configs[status] || configs.pendiente;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${config.bgColor} ${config.textColor} ${config.borderColor}`}>
        {config.icon}
        <span className="capitalize">{status}</span>
      </span>
    );
  };

  // Calcular estadísticas
  const stats = useMemo(() => {
    const totalAmount = payments.reduce((sum, payment) => sum + calculatePaymentAmount(payment), 0);
    const completedPayments = payments.filter(p => p.status === 'completado');
    const pendingPayments = payments.filter(p => p.status === 'pendiente');
    
    return {
      total: payments.length,
      completed: completedPayments.length,
      pending: pendingPayments.length,
      totalAmount,
      completedAmount: completedPayments.reduce((sum, p) => sum + calculatePaymentAmount(p), 0)
    };
  }, [payments]);

  const formatFullDate = (dateString) => {
    if (!dateString) return 'Sin fecha';

    // Extraemos solo la parte de la fecha sin hora
    const soloFecha = dateString.split('T')[0]; 

    return dayjs.tz(soloFecha, 'America/Santiago')
      .format('D [de] MMMM [de] YYYY');
  };


  return (
    <div className={`transition-all duration-300 ${show ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
      {show && (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
          {/* Header con gradiente */}
          <div className="bg-gradient-to-r from-[#09274A] to-[#0488AF] p-6 text-white">
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                  <FileText size={24} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Historial de Pagos</h2>
                  <p className="text-white/90 mt-1">{socio?.business_name}</p>
                </div>
              </div>
              <button
                onClick={onBack}
                className="flex items-center gap-2 px-4 py-2 bg-[#DC3465] hover:bg-[#c02e59] rounded-lg transition-colors backdrop-blur-sm"
              >
                <ArrowLeft size={16} />
                Volver
              </button>
            </div>
          </div>

          <div className="p-6">
            {/* Estadísticas */}
            <div className="grid grid-cols-3 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500 rounded-lg">
                    <Receipt className="text-white" size={16} />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-blue-700">{stats.total}</div>
                    <div className="text-sm text-blue-600">Total Pagos</div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl border border-green-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-500 rounded-lg">
                    <CheckCircle className="text-white" size={16} />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-700">{stats.completed}</div>
                    <div className="text-sm text-green-600">Completados</div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl border border-purple-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-500 rounded-lg">
                    <TrendingUp className="text-white" size={16} />
                  </div>
                  <div>
                    <div className="text-lg font-bold text-purple-700">{formatCurrency(stats.totalAmount)}</div>
                    <div className="text-sm text-purple-600">Total Monto</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Lista de pagos */}
            {payments.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <FileText className="text-gray-400" size={32} />
                </div>
                <p className="text-gray-500 text-lg">No hay pagos registrados</p>
                <p className="text-gray-400 text-sm mt-1">Los pagos aparecerán aquí una vez que se procesen</p>
              </div>
            ) : (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Registro de Transacciones</h3>
                
                {payments.map((pago, index) => {
                  const paymentAmount = calculatePaymentAmount(pago);
                  
                  return (
                    <div
                      key={pago.id}
                      className="bg-gradient-to-r from-gray-50 to-gray-50/50 border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition-all duration-200 hover:border-gray-300"
                      style={{
                        opacity: 1,
                        transform: 'translateX(0)',
                        transitionDelay: `${index * 100}ms`
                      }}
                    >
                      <div className="p-5">
                        <div className="flex items-start gap-4">
                          {/* Ícono del método de pago */}
                          <div className="p-3 bg-white rounded-xl shadow-sm border border-gray-200">
                            {getPaymentMethodIcon(pago.payment_method)}
                          </div>

                          {/* Contenido principal */}
                          <div className="flex-1 space-y-3">
                            {/* Header del pago */}
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="flex items-center gap-3 mb-2">
                                  <span className="text-xl font-bold text-gray-800">
                                    {formatCurrency(paymentAmount)}
                                  </span>
                                  {getStatusBadge(pago.status)}
                                </div>
                                
                                {/* Resumen de períodos */}
                                <div className="flex items-center gap-2 mb-2">
                                  <div className="flex flex-wrap gap-1">
                                    {pago.paymentSocialQuotas?.map((paymentQuota, index) => (
                                      <span
                                        key={index}
                                        className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-md font-medium"
                                      >
                                        {paymentQuota.socialQuota?.period} {paymentQuota.socialQuota?.year}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </div>

                              {/* Fecha */}
                              <div className="text-right text-sm text-gray-500">
                                <div className="flex items-center gap-1 mb-1">
                                  <Calendar size={14} />
                                  <span>Fecha de pago</span>
                                </div>
                                <span className="font-medium text-gray-700">
                                  {formatFullDate(pago.payment_date)}
                                </span>
                              </div>
                            </div>

                            {/* Detalles adicionales */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-gray-200">
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <FileText size={14} />
                                <span className="font-medium">Documento:</span>
                                <span className="font-mono text-gray-800">{pago.document_number}</span>
                              </div>
                              
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                {getPaymentMethodIcon(pago.payment_method)}
                                <span className="font-medium">Método:</span>
                                <span className="capitalize text-gray-800">{pago.payment_method}</span>
                              </div>
                            </div>

                            {/* Notas si existen */}
                            {pago.notes && (
                              <div className="flex items-start gap-2 text-sm text-gray-600 pt-2">
                                <FileText size={14} className="mt-0.5" />
                                <div>
                                  <span className="font-medium">Notas:</span>
                                  <span className="ml-2 text-gray-800">{pago.notes}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentHistory;